import { Layout } from '@/components/layouts/layout';
import { PhotoUpload } from '@/components/photo-upload';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '@/components/ui/tooltip';
import {
    useCreateInventoryItem,
    useDeleteInventoryItem,
    useInventoryItem,
    useUpdateInventoryItem,
} from '@/hooks/use-inventory';
import {
    getErrorMessage,
    mapRecordToFormValues,
    toNumberOrNull,
} from '@/lib/inventory-helpers';
import type { InventoryFormValues } from '@/types/inventory';
import { getActiveTenant } from '@/lib/tenant';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { ListingFeeHint, OfferFloorHint, ShippingEstimate, SoldProfitPreview } from '@/components/profit-preview';
import { LISTING_KEY_TO_PLATFORM, PLATFORM_SHIPPING_COST_DEFAULT, PLATFORM_SHIPPING_TYPE, calculatePlatformFees, platformLabelToKey } from '@/lib/platform-fees';
import { Copy, Trash2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import type { Control } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';

export const Route = createFileRoute('/inventory/$itemId')({
    component: EditItem,
});

const categoryOptions = [
    'Tops',
    'Bottoms',
    'Outerwear',
    'Dresses',
    'Activewear',
    'Shoes',
    'Accessories',
    'Bags',
    "Kid's",
    "Men's",
    "Women's",
    'Matching Sets',
    'Other',
];
const conditionOptions = ['New', 'Excellent', 'Good', 'Fair', 'Poor'];
const statusOptions = ['Listed', 'Sold', 'Unlisted'];
const platformOptions = [
    'Poshmark',
    'Depop',
    'eBay',
    'Mercari',
    'Etsy',
    'Facebook',
    'Vinted',
    'Other',
];

const platformListingOptions = [
    {
        key: 'fb',
        label: 'Facebook',
        status: 'fbStatus',
        price: 'fbListingPrice',
        url: 'fbUrl',
    },
    {
        key: 'poshmark',
        label: 'Poshmark',
        status: 'poshmarkStatus',
        price: 'poshmarkListingPrice',
        offerFloor: 'poshmarkOfferFloor',
        url: 'poshmarkUrl',
    },
    {
        key: 'depop',
        label: 'Depop',
        status: 'depopStatus',
        price: 'depopListingPrice',
        url: 'depopUrl',
    },
    {
        key: 'ebay',
        label: 'eBay',
        status: 'ebayStatus',
        price: 'ebayListingPrice',
        url: 'ebayUrl',
    },
    {
        key: 'mercari',
        label: 'Mercari',
        status: 'mercariStatus',
        price: 'mercariListingPrice',
        offerFloor: 'mercariOfferFloor',
        url: 'mercariUrl',
    },
    {
        key: 'etsy',
        label: 'Etsy',
        status: 'etsyStatus',
        price: 'etsyListingPrice',
        url: 'etsyUrl',
    },
    {
        key: 'vinted',
        label: 'Vinted',
        status: 'vintedStatus',
        price: 'vintedListingPrice',
        url: 'vintedUrl',
    },
] as const;

const emptyDefaults: InventoryFormValues = {
    category: '',
    brand: '',
    description: '',
    size: '',
    condition: '',
    cost: null,
    purchaseDate: '',
    purchaseLocation: '',
    notes: '',
    itemStatus: '',
    dateListed: '',
    dateSold: '',
    soldPlatform: '',
    salePrice: null,
    shippingCharged: null,
    platformFees: null,
    shippingCost: null,
    fbStatus: 'Not Listed',
    fbListingPrice: null,
    fbUrl: '',
    poshmarkStatus: 'Not Listed',
    poshmarkListingPrice: null,
    poshmarkOfferFloor: null,
    poshmarkUrl: '',
    depopStatus: 'Not Listed',
    depopListingPrice: null,
    depopUrl: '',
    ebayStatus: 'Not Listed',
    ebayListingPrice: null,
    ebayUrl: '',
    mercariStatus: 'Not Listed',
    mercariListingPrice: null,
    mercariOfferFloor: null,
    mercariUrl: '',
    etsyStatus: 'Not Listed',
    etsyListingPrice: null,
    etsyUrl: '',
    vintedStatus: 'Not Listed',
    vintedListingPrice: null,
    vintedUrl: '',
    purchaseTax: null,
    acquisitionCosts: null,
    weightOz: null,
    netProfit: null,
    daysToSell: null,
    imageUrls: [],
};

function EditItem() {
    const navigate = useNavigate();
    const { itemId } = Route.useParams();

    const { data, isLoading, isSuccess, isError, error } =
        useInventoryItem(itemId);
    const mutation = useUpdateInventoryItem();
    const duplicateMutation = useCreateInventoryItem();
    const deleteMutation = useDeleteInventoryItem();

    const form = useForm<InventoryFormValues>({
        defaultValues: emptyDefaults,
    });

    const watchedValues = form.watch();
    const [facebookLocal, setFacebookLocal] = useState(false);
    const [baseListingPrice, setBaseListingPrice] = useState<number | null>(null);
    const [targetROI, setTargetROI] = useState<number | null>(null);
    const [taxRatePct, setTaxRatePct] = useState<number | null>(null);
    useEffect(() => {
        getActiveTenant().then(t => setTaxRatePct(t.taxRatePct)).catch(() => {});
    }, []);

    const activePlatforms = platformListingOptions.filter((p) => {
        const status = watchedValues[p.status] as string | undefined;
        const price = watchedValues[p.price] as number | null;
        return status !== 'Not Listed' && price != null && price > 0;
    });
    const bestPlatformKey =
        activePlatforms.length >= 2
            ? activePlatforms.reduce<{ key: string; net: number } | null>((best, p) => {
                  const price = watchedValues[p.price] as number;
                  const platform = LISTING_KEY_TO_PLATFORM[p.key];
                  if (!platform) return best;
                  const fees = calculatePlatformFees(platform, price);
                  const net = price - (watchedValues.cost ?? 0) - fees.total;
                  return !best || net > best.net ? { key: p.key, net } : best;
              }, null)?.key ?? null
        : null;

    const isFacebook = watchedValues.soldPlatform === 'Facebook';
    const effectiveFacebookLocal = isFacebook && facebookLocal;

    useEffect(() => {
        if (isSuccess && data) {
            const values = mapRecordToFormValues(data);
            form.reset(values);
            const firstPrice = [
                values.poshmarkListingPrice,
                values.ebayListingPrice,
                values.mercariListingPrice,
                values.depopListingPrice,
                values.etsyListingPrice,
                values.fbListingPrice,
                values.vintedListingPrice,
            ].find((p): p is number => p !== null);
            if (firstPrice !== undefined) setBaseListingPrice(firstPrice);
        }
    }, [data, form, isSuccess]);

    const lastAutoFeeRef = useRef<number | null>(null);
    const lastAutoShippingRef = useRef<number | null | undefined>(undefined);
    const prevPlatformRef = useRef<string>('');
    const prevFacebookLocalRef = useRef<boolean>(false);
    useEffect(() => {
        const sp = watchedValues.soldPlatform ?? '';
        const price = watchedValues.salePrice;
        const shipping = watchedValues.shippingCharged ?? 0;
        const platformChanged = sp !== prevPlatformRef.current;
        const facebookLocalChanged = effectiveFacebookLocal !== prevFacebookLocalRef.current;
        prevPlatformRef.current = sp;
        prevFacebookLocalRef.current = effectiveFacebookLocal;
        if (!form.formState.isDirty) return;
        const platform = platformLabelToKey(sp);
        if (!platform) return;

        // Shipping default — update on platform change OR facebook local toggle
        if (platformChanged || facebookLocalChanged) {
            const shippingDefault =
                platform === 'facebook' && effectiveFacebookLocal
                    ? 0
                    : PLATFORM_SHIPPING_COST_DEFAULT[platform];
            const currentShipping = form.getValues('shippingCost');
            if (currentShipping === null || currentShipping === lastAutoShippingRef.current) {
                form.setValue('shippingCost', shippingDefault, { shouldDirty: true });
                lastAutoShippingRef.current = shippingDefault;
            }
        }

        // Fee calculation needs a price
        if (!price) return;
        const fees = calculatePlatformFees(platform, price, shipping, { localPickup: effectiveFacebookLocal });
        const rounded = Math.round(fees.total * 100) / 100;
        const currentFees = form.getValues('platformFees');
        if (platformChanged || currentFees === null || currentFees === lastAutoFeeRef.current) {
            form.setValue('platformFees', rounded, { shouldDirty: true });
            lastAutoFeeRef.current = rounded;
        }
    }, [watchedValues.soldPlatform, watchedValues.salePrice, watchedValues.shippingCharged, effectiveFacebookLocal, form]);

    const onSubmit = form.handleSubmit(async (values) => {
        try {
            await mutation.mutateAsync({ id: itemId, values });
            toast.success('Item updated.');
            await navigate({ to: '/inventory', search: { q: '' } });
        } catch (submitError) {
            toast.error(getErrorMessage(submitError));
        }
    });

    const onDuplicate = async () => {
        try {
            const values = buildDuplicateValues(form.getValues());
            const result = await duplicateMutation.mutateAsync(values);
            const newId = result?.[0]?.Id;
            toast.success('Item duplicated.');
            if (typeof newId === 'string') {
                await navigate({
                    to: '/inventory/$itemId',
                    params: { itemId: newId },
                });
                return;
            }
            await navigate({ to: '/inventory', search: { q: '' } });
        } catch (duplicateError) {
            toast.error(getErrorMessage(duplicateError));
        }
    };

    const onDelete = async () => {
        try {
            await deleteMutation.mutateAsync(itemId);
            toast.success('Item deleted.');
            await navigate({ to: '/inventory', search: { q: '' } });
        } catch (deleteError) {
            toast.error(getErrorMessage(deleteError));
        }
    };

    return (
        <Layout>
            <div className="mx-auto w-full max-w-3xl space-y-6 px-4 py-6">
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                        <h1 className="text-2xl font-semibold">Edit Item</h1>
                        <p className="text-sm text-muted-foreground">
                            Update the details for this inventory item.
                        </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={onDuplicate}
                                    disabled={
                                        isLoading ||
                                        isError ||
                                        duplicateMutation.isPending
                                    }
                                    className="text-muted-foreground hover:text-foreground"
                                    aria-label="Duplicate item"
                                >
                                    {duplicateMutation.isPending ? (
                                        'Duplicating…'
                                    ) : (
                                        <Copy className="size-4" />
                                    )}
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent side="bottom">
                                Duplicate Item
                            </TooltipContent>
                        </Tooltip>
                        <AlertDialog>
                            <AlertDialogTrigger asChild>
                                <Button
                                    type="button"
                                    variant="destructive"
                                    size="sm"
                                    disabled={
                                        isLoading ||
                                        isError ||
                                        deleteMutation.isPending
                                    }
                                >
                                    <Trash2 className="size-4" />
                                    Delete
                                </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                                <AlertDialogHeader>
                                    <AlertDialogTitle>
                                        Delete item?
                                    </AlertDialogTitle>
                                    <AlertDialogDescription>
                                        This will permanently delete this
                                        inventory item and cannot be undone.
                                    </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                    <AlertDialogCancel
                                        disabled={deleteMutation.isPending}
                                    >
                                        Cancel
                                    </AlertDialogCancel>
                                    <AlertDialogAction
                                        variant="destructive"
                                        onClick={onDelete}
                                        disabled={deleteMutation.isPending}
                                    >
                                        {deleteMutation.isPending
                                            ? 'Deleting…'
                                            : 'Delete'}
                                    </AlertDialogAction>
                                </AlertDialogFooter>
                            </AlertDialogContent>
                        </AlertDialog>
                        <Button asChild variant="outline">
                            <Link to="/inventory" search={{ q: '' }}>
                                Back to Inventory
                            </Link>
                        </Button>
                    </div>
                </div>

                {!itemId && (
                    <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-6 text-sm text-destructive">
                        Invalid inventory id.
                    </div>
                )}

                {itemId && isLoading && (
                    <div className="space-y-4 rounded-lg border border-border bg-card p-6 shadow-sm">
                        <Skeleton className="h-6 w-40" />
                        <Skeleton className="h-10 w-full" />
                        <Skeleton className="h-10 w-full" />
                        <Skeleton className="h-24 w-full" />
                    </div>
                )}

                {itemId && isError && (
                    <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-6 text-sm text-destructive">
                        {(error as Error).message}
                    </div>
                )}

                <Form {...form}>
                    <form
                        className="rounded-lg border border-border bg-card shadow-sm"
                        onSubmit={onSubmit}
                    >
                        <Tabs defaultValue="item">
                            <div className="px-6 pt-6">
                                <TabsList className="w-full">
                                    <TabsTrigger value="item">Item</TabsTrigger>
                                    <TabsTrigger value="purchase">Purchase</TabsTrigger>
                                    <TabsTrigger value="listing">Listing</TabsTrigger>
                                    <TabsTrigger value="sold">Sold</TabsTrigger>
                                </TabsList>
                            </div>

                            <TabsContent value="item" className="px-6 pb-2 pt-4">
                                <div className="grid gap-4 sm:grid-cols-2 items-start">
                                    <SelectField
                                        control={form.control}
                                        name="itemStatus"
                                        label="Item Status"
                                        options={statusOptions}
                                    />

                                    <SelectField
                                        control={form.control}
                                        name="category"
                                        label="Category"
                                        options={categoryOptions}
                                        placeholder="Select"
                                    />

                                    <InputField
                                        control={form.control}
                                        name="brand"
                                        label="Brand"
                                        placeholder="Lululemon"
                                    />

                                    <InputField
                                        control={form.control}
                                        name="size"
                                        label="Size"
                                        placeholder="M, 6, 25, 26"
                                    />

                                    <NumberField
                                        control={form.control}
                                        name="weightOz"
                                        label="Weight (oz)"
                                        placeholder="12.5"
                                    />

                                    <SelectField
                                        control={form.control}
                                        name="condition"
                                        label="Condition"
                                        options={conditionOptions}
                                        placeholder="Select"
                                    />

                                    <TextareaField
                                        control={form.control}
                                        name="description"
                                        label="Description"
                                        className="sm:col-span-2"
                                        placeholder="Black speed up shorts"
                                    />

                                    <div className="sm:col-span-2 space-y-2">
                                        <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                                            Photos
                                        </label>
                                        <PhotoUpload
                                            value={watchedValues.imageUrls ?? []}
                                            onChange={(urls) =>
                                                form.setValue('imageUrls', urls, {
                                                    shouldDirty: true,
                                                })
                                            }
                                            folder={`stockwright/${itemId}`}
                                        />
                                    </div>
                                </div>
                            </TabsContent>

                            <TabsContent value="purchase" className="px-6 pb-2 pt-4">
                                <div className="grid gap-4 sm:grid-cols-2 items-start">
                                    <InputField
                                        control={form.control}
                                        name="purchaseLocation"
                                        label="Purchase Location"
                                        placeholder="Goodwill"
                                    />

                                    <InputField
                                        control={form.control}
                                        name="purchaseDate"
                                        label="Purchase Date"
                                        type="date"
                                    />

                                    <NumberField
                                        control={form.control}
                                        name="cost"
                                        label="Cost"
                                        placeholder="7.99"
                                    />

                                    <NumberField
                                        control={form.control}
                                        name="purchaseTax"
                                        label="Sales Tax Paid"
                                        placeholder="0.62"
                                    />

                                    <NumberField
                                        control={form.control}
                                        name="acquisitionCosts"
                                        label="Other Acquisition Costs"
                                        placeholder="Gas, fees, etc."
                                    />
                                </div>
                            </TabsContent>

                            <TabsContent value="listing" className="px-6 pb-2 pt-4">
                                <div className="grid gap-4 sm:grid-cols-2 items-start">
                                    <InputField
                                        control={form.control}
                                        name="dateListed"
                                        label="Date Listed"
                                        type="date"
                                    />

                                    <div className="space-y-2">
                                        <label className="text-sm font-medium leading-none">
                                            Listing Price
                                        </label>
                                        <Input
                                            type="number"
                                            step="0.01"
                                            placeholder="0.00"
                                            value={baseListingPrice ?? ''}
                                            onChange={(e) =>
                                                setBaseListingPrice(
                                                    toNumberOrNull(e.target.value),
                                                )
                                            }
                                            className="dark:bg-input/30"
                                        />
                                    </div>

                                    <div className="space-y-2">
                                        <label className="text-sm font-medium leading-none">
                                            Target ROI %
                                        </label>
                                        <Input
                                            type="number"
                                            step="1"
                                            placeholder="e.g. 30"
                                            value={targetROI ?? ''}
                                            onChange={(e) =>
                                                setTargetROI(
                                                    toNumberOrNull(e.target.value),
                                                )
                                            }
                                            className="dark:bg-input/30"
                                        />
                                    </div>

                                    <div className="sm:col-span-2 space-y-3">
                                        <p className="text-xs text-muted-foreground">
                                            Toggle a platform to list it. Each platform defaults to the price above and can be overridden individually.
                                        </p>
                                        {platformListingOptions.map((platform) => {
                                            const statusValue =
                                                (watchedValues?.[platform.status] as
                                                    | string
                                                    | undefined) ?? 'Not Listed';
                                            const isActive =
                                                statusValue !== 'Not Listed';

                                            return (
                                                <div
                                                    key={platform.key}
                                                    className="space-y-3 rounded-md border border-border/60 bg-background p-3 dark:bg-input/30"
                                                >
                                                    <FormField
                                                        control={form.control}
                                                        name={platform.status}
                                                        render={({ field }) => (
                                                            <FormItem className="space-y-2">
                                                                <div className="flex items-center gap-3 text-sm font-medium">
                                                                    <FormControl>
                                                                        <Checkbox
                                                                            checked={field.value !== 'Not Listed'}
                                                                            onCheckedChange={(checked) => {
                                                                                field.onChange(
                                                                                    checked ? 'Active' : 'Not Listed',
                                                                                );
                                                                                if (
                                                                                    checked &&
                                                                                    form.getValues(platform.price) === null &&
                                                                                    baseListingPrice !== null
                                                                                ) {
                                                                                    form.setValue(
                                                                                        platform.price,
                                                                                        baseListingPrice,
                                                                                        { shouldDirty: true },
                                                                                    );
                                                                                }
                                                                            }}
                                                                        />
                                                                    </FormControl>
                                                                    <span>{platform.label}</span>
                                                                    <span className="text-xs font-normal text-muted-foreground">
                                                                        {statusValue}
                                                                    </span>
                                                                    {platform.key === bestPlatformKey && (
                                                                        <span className="ml-auto text-xs font-medium text-green-600 dark:text-green-400">
                                                                            Best payout
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                <FormMessage />
                                                            </FormItem>
                                                        )}
                                                    />
                                                    {isActive && (
                                                        <div className="space-y-3">
                                                            <NumberField
                                                                control={form.control}
                                                                name={platform.price}
                                                                label={`${platform.label} Price`}
                                                                placeholder="0.00"
                                                            />
                                                            <ListingFeeHint
                                                                platformKey={platform.key}
                                                                listingPrice={(watchedValues[platform.price] as number | null) ?? null}
                                                                cost={watchedValues.cost}
                                                                purchaseTax={watchedValues.purchaseTax}
                                                                acquisitionCosts={watchedValues.acquisitionCosts}
                                                                targetROIPct={targetROI}
                                                            />
                                                            {PLATFORM_SHIPPING_TYPE[LISTING_KEY_TO_PLATFORM[platform.key]] === 'seller-pays' && (
                                                                <ShippingEstimate
                                                                    platform={LISTING_KEY_TO_PLATFORM[platform.key]}
                                                                    weightOz={watchedValues.weightOz}
                                                                />
                                                            )}
                                                            {'offerFloor' in platform && (
                                                                <>
                                                                    <NumberField
                                                                        control={form.control}
                                                                        name={platform.offerFloor}
                                                                        label="Min Acceptable Offer"
                                                                        placeholder="0.00"
                                                                    />
                                                                    <OfferFloorHint
                                                                        platformKey={platform.key}
                                                                        offerFloor={(watchedValues[platform.offerFloor] as number | null) ?? null}
                                                                        cost={watchedValues.cost}
                                                                        purchaseTax={watchedValues.purchaseTax}
                                                                        acquisitionCosts={watchedValues.acquisitionCosts}
                                                                    />
                                                                </>
                                                            )}
                                                            <InputField
                                                                control={form.control}
                                                                name={platform.url}
                                                                label={`${platform.label} URL`}
                                                                placeholder="https://"
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </TabsContent>

                            <TabsContent value="sold" className="px-6 pb-2 pt-4">
                                <div className="grid gap-4 sm:grid-cols-2 items-start">
                                    <InputField
                                        control={form.control}
                                        name="dateSold"
                                        label="Date Sold"
                                        type="date"
                                    />

                                    <SelectField
                                        control={form.control}
                                        name="soldPlatform"
                                        label="Sold Platform"
                                        options={platformOptions}
                                        placeholder="Select"
                                    />

                                    {isFacebook && (
                                        <div className="sm:col-span-2 flex items-center gap-2">
                                            <Checkbox
                                                id="facebook-local-edit"
                                                checked={facebookLocal}
                                                onCheckedChange={(checked) => setFacebookLocal(checked === true)}
                                            />
                                            <label htmlFor="facebook-local-edit" className="text-sm font-medium cursor-pointer select-none">
                                                Local pickup (no fee)
                                            </label>
                                        </div>
                                    )}

                                    <NumberField
                                        control={form.control}
                                        name="salePrice"
                                        label="Sale Price"
                                        placeholder="35.00"
                                    />

                                    <NumberField
                                        control={form.control}
                                        name="shippingCharged"
                                        label="Shipping Charged"
                                        placeholder="0.00"
                                    />

                                    <NumberField
                                        control={form.control}
                                        name="platformFees"
                                        label="Platform Fees"
                                        placeholder="8.00"
                                    />

                                    <NumberField
                                        control={form.control}
                                        name="shippingCost"
                                        label="Shipping Cost"
                                        placeholder="5.25"
                                    />

                                    <ShippingEstimate
                                        platform={platformLabelToKey(watchedValues.soldPlatform ?? '')}
                                        weightOz={watchedValues.weightOz}
                                        onUseEstimate={(cost) =>
                                            form.setValue('shippingCost', cost, { shouldDirty: true })
                                        }
                                    />

                                    <SoldProfitPreview
                                        salePrice={watchedValues.salePrice}
                                        shippingCharged={watchedValues.shippingCharged}
                                        cost={watchedValues.cost}
                                        purchaseTax={watchedValues.purchaseTax}
                                        acquisitionCosts={watchedValues.acquisitionCosts}
                                        platformFees={watchedValues.platformFees}
                                        shippingCost={watchedValues.shippingCost}
                                        soldPlatform={watchedValues.soldPlatform ?? ''}
                                        localPickup={effectiveFacebookLocal}
                                        taxRatePct={taxRatePct}
                                    />

                                    <TextareaField
                                        control={form.control}
                                        name="notes"
                                        label="Notes"
                                        className="sm:col-span-2"
                                        placeholder="Anything special about this item?"
                                    />
                                </div>
                            </TabsContent>
                        </Tabs>

                        {mutation.isError && (
                            <div className="mx-6 rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
                                {(mutation.error as Error).message}
                            </div>
                        )}

                        <div className="flex flex-wrap items-center justify-end gap-3 border-t border-border px-6 py-4 mt-2">
                            <Button
                                type="button"
                                variant="ghost"
                                onClick={() => {
                                    return form.reset(
                                        data
                                            ? mapRecordToFormValues(data)
                                            : emptyDefaults,
                                    );
                                }}
                            >
                                Reset
                            </Button>
                            <Button type="submit" disabled={mutation.isPending}>
                                {mutation.isPending
                                    ? 'Saving…'
                                    : 'Save Changes'}
                            </Button>
                        </div>
                    </form>
                </Form>
            </div>
        </Layout>
    );
}

function buildDuplicateValues(
    values: InventoryFormValues,
): InventoryFormValues {
    const nextStatus =
        values.itemStatus === 'Sold' ? 'Listed' : values.itemStatus;
    return {
        ...values,
        itemStatus: nextStatus,
        dateSold: null,
        soldPlatform: '',
        salePrice: null,
        shippingCharged: null,
        platformFees: null,
        shippingCost: null,
        imageUrls: [],
    };
}

function SelectField({
    control,
    name,
    label,
    options,
    placeholder,
}: {
    control: Control<InventoryFormValues>;
    name: keyof InventoryFormValues;
    label: string;
    options: string[];
    placeholder?: string;
}) {
    return (
        <FormField
            control={control}
            name={name}
            render={({ field }) => {
                const value = (field.value ?? '') as string;

                return (
                    <FormItem>
                        <FormLabel>{label}</FormLabel>

                        <Select value={value} onValueChange={field.onChange}>
                            <FormControl>
                                <SelectTrigger className="w-full">
                                    <SelectValue
                                        placeholder={placeholder ?? 'Select'}
                                    />
                                </SelectTrigger>
                            </FormControl>

                            <SelectContent>
                                {options.map((option) => (
                                    <SelectItem key={option} value={option}>
                                        {option}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>

                        <FormMessage />
                    </FormItem>
                );
            }}
        />
    );
}

function InputField({
    control,
    name,
    label,
    type = 'text',
    placeholder,
    className,
}: {
    control: Control<InventoryFormValues>;
    name: keyof InventoryFormValues;
    label: string;
    type?: string;
    placeholder?: string;
    className?: string;
}) {
    return (
        <FormField
            control={control}
            name={name}
            render={({ field }) => {
                const isDate = type === 'date';
                const value =
                    typeof field.value === 'string' ? field.value : '';
                return (
                    <FormItem className={className}>
                        <FormLabel>{label}</FormLabel>
                        <FormControl>
                            {isDate ? (
                                <div className="relative">
                                    <Input
                                        type={type}
                                        placeholder={placeholder}
                                        value={value}
                                        onChange={(event) =>
                                            field.onChange(
                                                event.target.value === ''
                                                    ? null
                                                    : event.target.value,
                                            )
                                        }
                                        className="pr-16 dark:bg-input/30"
                                    />
                                    {value ? (
                                        <button
                                            type="button"
                                            onClick={() => field.onChange(null)}
                                            className="absolute right-2 top-1/2 -translate-y-1/2 text-xs font-medium text-muted-foreground hover:text-foreground"
                                            aria-label={`Clear ${label}`}
                                        >
                                            Clear
                                        </button>
                                    ) : null}
                                </div>
                            ) : (
                                <Input
                                    type={type}
                                    placeholder={placeholder}
                                    value={value}
                                    onChange={(event) =>
                                        field.onChange(event.target.value)
                                    }
                                    className="dark:bg-input/30"
                                />
                            )}
                        </FormControl>
                        <FormMessage />
                    </FormItem>
                );
            }}
        />
    );
}

function NumberField({
    control,
    name,
    label,
    placeholder,
}: {
    control: Control<InventoryFormValues>;
    name: keyof InventoryFormValues;
    label: string;
    placeholder?: string;
}) {
    return (
        <FormField
            control={control}
            name={name}
            render={({ field }) => (
                <FormItem>
                    <FormLabel>{label}</FormLabel>
                    <FormControl>
                        <Input
                            type="number"
                            step="0.01"
                            placeholder={placeholder}
                            value={field.value ?? ''}
                            onChange={(event) =>
                                field.onChange(
                                    toNumberOrNull(event.target.value),
                                )
                            }
                            className="dark:bg-input/30"
                        />
                    </FormControl>
                    <FormMessage />
                </FormItem>
            )}
        />
    );
}

function TextareaField({
    control,
    name,
    label,
    placeholder,
    className,
}: {
    control: Control<InventoryFormValues>;
    name: keyof InventoryFormValues;
    label: string;
    placeholder?: string;
    className?: string;
}) {
    return (
        <FormField
            control={control}
            name={name}
            render={({ field }) => (
                <FormItem className={className}>
                    <FormLabel>{label}</FormLabel>
                    <FormControl>
                        <Textarea
                            placeholder={placeholder}
                            value={field.value ?? ''}
                            onChange={(event) =>
                                field.onChange(event.target.value)
                            }
                        />
                    </FormControl>
                    <FormMessage />
                </FormItem>
            )}
        />
    );
}
